/**
 * What `sysAdminGetOnboarding` returns.
 *
 * A mirror of `functions/src/admin/onboarding/types.ts`, because the web app
 * cannot import from `functions/`. Held to it by `onboardingShape.guard.test.ts`
 * the way the contact roles and organization types are.
 */

export const STAGE_IDS = [
  'organization', 'contacts', 'agreements', 'billing', 'authentication',
  'administrators', 'configuration', 'readiness', 'goLive',
] as const;
export type StageId = (typeof STAGE_IDS)[number];

export type Owner = 'customer' | 'standingdocs';
export type RequirementStatus = 'met' | 'missing' | 'warning' | 'notRequired';
export type StageStatus =
  | 'notStarted' | 'inProgress' | 'waitingOnCustomer' | 'waitingOnStandingDocs'
  | 'needsAttention' | 'complete' | 'notRequired';

export interface Requirement {
  key: string;
  label: string;
  status: RequirementStatus;
  blocking: boolean;
  detail: string;
  owner: Owner;
  contactRole: string | null;
  stage: StageId;
}

export interface Stage {
  id: StageId;
  title: string;
  blurb: string;
  status: StageStatus;
  requirements: Requirement[];
  blockingOpen: number;
}

export interface Person {
  id: string;
  name: string;
  email: string;
  title: string | null;
  phone: string | null;
  roles: string[];
  /** The roles this person is primary for: who documents go to by default. */
  primaryRoles?: string[];
  /** 'customer' only when the intake form stamped it. */
  source: 'customer' | 'internal';
}

export interface Intake {
  submittedAt: string | null;
  updatedAt: string | null;
  submitter: { name: string | null; email: string | null };
  complete: boolean;
  outstanding: string[];
  legalName: string | null;
  address: string | null;
  locationCount: number | null;
  organizationType: string | null;
  targetGoLiveDate: string | null;
  needsBaa: boolean | null;
  needsSecurityReview: boolean | null;
  poRequired: boolean | null;
  poNumber: string | null;
  suggestedPlan: string | null;
  /** Answers we entered, by "section.field", while they still hold what we entered. */
  staffFields?: Record<string, StaffField>;
  /** Set when we marked the form returned on their behalf. */
  returnedBy?: (StaffField & { detail: string | null }) | null;
}

/** How we came by an answer we entered for the customer. */
export interface StaffField {
  method: string;
  detail?: string | null;
  givenByName?: string | null;
  givenByEmail?: string | null;
  byEmail: string | null;
  at: string;
}

export interface Onboarding {
  orgId: string;
  orgName: string;
  targetGoLiveDate: string | null;
  live: boolean;
  stages: Stage[];
  nextAction: {
    text: string; stage: StageId; owner: Owner;
    contactRole: string | null; requirementKey: string | null;
  };
  attention: Requirement[];
  requiredStages: number;
  completeStages: number;
  ready: boolean;
  intake: Intake | null;
  intakeLinkSent: boolean;
  /** The newest live onboarding-form link. */
  intakeLink?: { sentTo: string | null; sentAt: string | null; expiresAt: string | null; expired: boolean } | null;
  people: Person[];
  contract: {
    exists: boolean; plan: string | null; facilityCount: number | null;
    annualAmount: number | null; termStart: string | null; termEnd: string | null;
    poNumber: string | null;
  };
  suggestedPlan: string | null;
  domains: { domain: string; verified: boolean }[];
  sso: { enabled: boolean; authMode: string; ready: boolean };
  addonsEnabled: string[];
  sysAdmins: { uid: string; email: string; displayName: string | null; enrolled: boolean }[];
  pendingSysAdminInvites: { id: string; email: string }[];
  remindersOn: boolean;
  /** Raw paperwork rows by document key, as `sysAdminSetOrgPaperwork` stores them. */
  paperwork?: Record<string, { status?: string; at?: string | null; byEmail?: string | null } | undefined>;
  policiesTotal: number;
  staffMembers: number;
  orgStatus: string;
  signing: { configured: boolean; environment: string };
}

/** The one vocabulary, in the words the screen uses. */
export const STAGE_STATUS_LABEL: Record<StageStatus, string> = {
  notStarted: 'Not started',
  inProgress: 'In progress',
  waitingOnCustomer: 'Waiting on customer',
  waitingOnStandingDocs: 'Waiting on us',
  needsAttention: 'Needs attention',
  complete: 'Complete',
  notRequired: 'Not required',
};

/** Which badge tone each status wears. `page.tag` plus one of these. */
export const STAGE_STATUS_TONE: Record<StageStatus, 'ok' | 'warn' | 'danger' | 'info' | 'neutral'> = {
  notStarted: 'neutral',
  inProgress: 'info',
  waitingOnCustomer: 'warn',
  waitingOnStandingDocs: 'warn',
  needsAttention: 'danger',
  complete: 'ok',
  notRequired: 'neutral',
};
